import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLines } from "@/components/Reveal";
import { ArrowDown } from "lucide-react";
import FloatingDrinks from "@/components/FloatingDrinks";

export default function Hero() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.25], [0, 120]);
  const opacity = useTransform(scrollYProgress, [0, 0.18], [1, 0]);

  return (
    <section
      id="top"
      className="relative h-screen w-full overflow-hidden bg-[#050505]"
      data-testid="hero-section"
    >
      {/* Floating cocktails layer */}
      <div className="absolute inset-0 z-0">
        <FloatingDrinks />
      </div>

      {/* vignette + glow */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(circle_at_50%_45%,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="absolute inset-0 z-[1] pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_35%,#050505_85%)]" />

      {/* content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-20"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="text-xs md:text-sm uppercase tracking-[0.4em] text-gold mb-6"
          data-testid="hero-overline"
        >
          Mobile Bartending · Tri-State
        </motion.p>

        <h1 className="font-heading font-medium text-[15vw] md:text-[13vw] lg:text-[10.5vw] leading-[0.82] tracking-tighter">
          <MaskedLines
            lines={["The Bar", <span key="l2" className="italic gold-shimmer">Comes to You</span>]}
            stagger={0.14}
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1 }}
          className="mt-9 flex flex-col md:flex-row md:items-end gap-8 max-w-4xl"
        >
          <p className="text-[#a0a0a0] text-base md:text-lg font-light leading-relaxed max-w-md">
            Bespoke cocktail experiences crafted at your event. We build the bar, the menu, and
            the moment — anywhere you gather.
          </p>
          <a
            href="#booking"
            data-testid="hero-book-cta"
            className="group inline-flex items-center gap-3 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.25em] hover:bg-[#e5c158] active:scale-[0.98] transition-all duration-300 whitespace-nowrap"
          >
            Reserve Your Bar
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-[#a0a0a0]"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={16} className="text-gold" />
        </motion.div>
      </motion.div>
    </section>
  );
}
